import type { TeamUsage, UserUsage } from '@/api-models/usage';

/**
 * How the usage charts turn members into colored series.
 *
 * Two rules drive everything here:
 *
 * 1. **Slots are never cycled.** There are six validated categorical hues; a
 *    seventh member folds into a neutral "Other" band rather than reusing a hue
 *    that already means someone else.
 * 2. **Color follows the person, not their rank.** Within the named set, slots
 *    are handed out in `user_id` order, not spend order — so changing the date
 *    range or filtering to one member doesn't repaint everyone. Which members are
 *    *named* is still spend-ranked (the biggest spenders are the ones worth
 *    naming), so a member crossing into or out of the top six can shift the
 *    assignment. That is the one instability left, and it beats the alternative
 *    of two people sharing a color.
 */
export const SERIES_SLOTS = 6;

const SLOT_VARS = [
  'var(--color-series-1)',
  'var(--color-series-2)',
  'var(--color-series-3)',
  'var(--color-series-4)',
  'var(--color-series-5)',
  'var(--color-series-6)',
] as const;

export const OTHER_COLOR = 'var(--color-series-other)';

/** Key used for the "everyone past the named slots" band and for unowned rows. */
export const OTHER_KEY = '__other__';

export interface Series {
  /** `user_id`, or {@link OTHER_KEY}. */
  key: string;
  label: string;
  color: string;
  total_micro_usd: number;
}

function displayName(u: UserUsage): string {
  return u.name || u.email || 'Unattributed';
}

/**
 * Build the chart's series list from a ledger.
 *
 * Members with no spend in the window are dropped — a zero-height band is a
 * legend entry that explains nothing.
 */
export function buildSeries(usage: TeamUsage): Series[] {
  const spenders = usage.by_user.filter((u) => u.total_micro_usd !== 0);
  const named = spenders.slice(0, SERIES_SLOTS).filter((u) => u.user_id !== null);
  const rest = spenders.filter((u) => !named.includes(u));

  // Slot order is by id, so a person keeps their color as the window changes.
  const byId = [...named].sort((a, b) => (a.user_id! < b.user_id! ? -1 : 1));
  const colors = new Map(byId.map((u, i) => [u.user_id!, SLOT_VARS[i % SLOT_VARS.length]!]));

  const series: Series[] = named.map((u) => ({
    key: u.user_id!,
    label: displayName(u),
    color: colors.get(u.user_id!)!,
    total_micro_usd: u.total_micro_usd,
  }));

  if (rest.length > 0) {
    series.push({
      key: OTHER_KEY,
      label: rest.length === 1 ? displayName(rest[0]!) : `Other (${rest.length})`,
      color: OTHER_COLOR,
      total_micro_usd: rest.reduce((sum, u) => sum + u.total_micro_usd, 0),
    });
  }

  // Drawn and legended in spend order; only the *color* is rank-independent.
  return series.sort((a, b) => b.total_micro_usd - a.total_micro_usd);
}

/**
 * A day's spend split across the series, in the series' own order.
 *
 * Slices belonging to no named series (the tail members, and rows written
 * without an owning user) collapse into the "Other" band, so the stack always
 * adds up to the day's total — a bar that didn't would contradict the axis it is
 * drawn against.
 */
export function stackDay(perUser: { user_id: string | null; total_micro_usd: number }[], series: Series[]): number[] {
  const named = new Set(series.map((s) => s.key));
  const totals = new Map<string, number>();

  for (const slice of perUser) {
    const key = slice.user_id && named.has(slice.user_id) ? slice.user_id : OTHER_KEY;
    totals.set(key, (totals.get(key) ?? 0) + slice.total_micro_usd);
  }
  return series.map((s) => totals.get(s.key) ?? 0);
}
